import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { Platform, StyleSheet, View } from "react-native";
import { useOnBeforeClose } from "react-native-bottom-sheet-stack";
import type { CloseStop } from "@libs/buy-on-behalf-client/purchase";
import type { CheckoutGoods } from "@libs/checkout-core";
import { useSheetFunnel } from "@libs/shared-ui";
import type {
  PurchaseFunnelStep,
  PurchaseSheetScreen,
  SheetFunnelOpening,
} from "@libs/shared-ui";
import { LOADING_BUTTON } from "./native/sheetButton";
import type { CheckoutChrome, SheetButtonState } from "./native/sheetButton";
import { PurchaseSuccess } from "./native/SuccessView";
import {
  CodeStep,
  ContactField,
  isEmailValid,
  useSheetSignIn,
} from "./SheetContact";
import { TicketsSheetFrame } from "./TicketsSheetFrame";
import { askStop, sayTooLate } from "./askStop";

/**
 * Every purchase in the app is one flow, in the one light sheet stacked
 * over the access Details (TicketsSheet): an engine event's tickets
 * (EngineTicketsSheet), a vendor's tickets bought on the fan's behalf
 * (VendorTicketsSheet) and a music unlock (MusicUnlockSheet) alike.
 *
 *   1. what is bought   tickets chosen from the tiers, or the one item
 *                       summed up, with nothing to choose
 *   2. who it is for    the fan's sign-in: natively Continue asks for the
 *                       app's sign-in first; on app-web, signed out, the
 *                       address and its code are asked for under what is
 *                       bought, and the checkout opens once they are in
 *   3. the checkout     opened for the fan (the tickets held, a vendor's
 *                       journey readied, the unlock's hold), its shape
 *                       drawn meanwhile, then drawn in the sheet
 *   4. the end          the checkout's own ("You're going", "It's yours"),
 *                       or the same success screen when the order was
 *                       complete before any checkout (a free unlock)
 *
 * The purchase funnel's steps (LIS-1110) are told to the purchase
 * (`onFunnelStep`), as Link in Bio's sheet tells its host, by the same
 * rules (`useSheetFunnel`): the sheet opened; the fan went on with what is
 * bought (Continue, or the code being up), once per opening; the checkout
 * was asked for, signed in; the sheet went, on the screen it was on.
 *
 * One button through it all: Continue, "Loading checkout" while the
 * checkout opens and gets ready, then the checkout's Pay and the payment's
 * status — never swapped for another. Back from the checkout returns to
 * what is bought. A purchase says only what differs (`PurchaseFlow`); the
 * steps, their order and the sheet are this.
 */

/** The fan buying, as the purchase reaches the app's sign-in. */
export type PurchaseBuyer<User extends object = object> = {
  /** Signed in: the app's sign-in (Firebase's user, a new object for a new
   *  sign-in even to the same account), or null. */
  user: User | null;
  /** Their address, drawn in the checkout's shape before it says it. */
  email: string | null;
  /** Their account has loaded: until it has, app-web asks for the address
   *  and a code in the sheet. */
  accountLoaded: boolean;
  /** The app's own sign-in: natively, before any checkout. */
  signIn: () => void;
};

/** Step 1: what is bought. */
export type PurchaseChoice = {
  /** Loaded: Continue and the contact step are offered. */
  ready: boolean;
  /** Continue's words, with the total ("Continue · £12.00"). */
  words: string;
  /** Nothing to continue with: none chosen, outside a limit, details the
   *  event requires not given, a refusal another try cannot change. */
  disabled: boolean;
  /** Drawn above the contact step (its Continue is the sheet's button).
   *  `locked` while the code is up: nothing chosen may change. */
  render: (step: { locked: boolean }) => ReactNode;
  /** Drawn under what is bought: what the total on Continue includes (a
   *  product's UK delivery). */
  note?: ReactNode;
  /** What is chosen, for the purchase funnel: how many, and what they
   *  cost before fees (booking, order and delivery fees); `totalMinor` and
   *  `currency` null when it is free. */
  selection: PurchaseSelection;
};

/** What the fan goes on with (`purchase_items_choose`). */
export type PurchaseSelection = Omit<
  Extract<PurchaseFunnelStep, { step: "items_choose" }>,
  "step"
>;

/** A selection's quantity and its total before fees, as the funnel says
 *  it: no total or currency when it costs nothing. */
export const purchaseSelection = (
  quantity: number,
  totalMinor: number,
  currency: string,
): PurchaseSelection => ({
  quantity,
  totalMinor: totalMinor > 0 ? totalMinor : null,
  currency: totalMinor > 0 ? currency : null,
});

/** What opening the checkout came to. */
export type PurchaseOpened<C> =
  /** A checkout to pay in. */
  | { kind: "checkout"; checkout: C }
  /** Nothing was left to pay: the order is the fan's already (a free
   *  unlock). The success screen, with the order's card; the receipt went
   *  to the fan's address. */
  | { kind: "complete"; order: ReactNode }
  /** Nothing to buy here after all (it is the fan's already, or another
   *  account's sheet now): the sheet closes. */
  | { kind: "close" }
  /** Not opened: back to what is bought, which says why. */
  | { kind: "back" };

/** What the sheet gives the checkout it draws. */
export type CheckoutHost = {
  /** Back to what is bought. */
  back: () => void;
  /** Done: the sheet closes. */
  close: () => void;
  /** The sheet's button, what follows it and whether to keep the sheet's
   *  height: told by the checkout as they change. */
  chrome: (chrome: CheckoutChrome) => void;
  /** Something is moving the fan may not walk away from: the sheet will
   *  not close while it is. */
  holding: (holding: boolean) => void;
  /** How closing the sheet stops the purchase, at each change of its
   *  moment: while it may (after Buy, before Pay) the close asks "Stop this
   *  purchase?" first; once Pay reached it, too late to stop. Null
   *  otherwise. */
  stoppable: (stop: CloseStop | null) => void;
  /** A merch order's delivery is being chosen, before the checkout: the
   *  screen the funnel says the sheet is on. */
  delivering: (delivering: boolean) => void;
  /** The checkout is showing the confirmed purchase. */
  succeeded: () => void;
};

/** Step 3: the checkout, `C` being the purchase's open one (an engine
 *  session, a vendor's, a music unlock's hold). */
export type PurchaseCheckout<C> = {
  /** Opens it for what was chosen, for the fan. */
  open: () => PurchaseOpened<C> | Promise<PurchaseOpened<C>>;
  /** Which checkout it is: another one starts from nothing. */
  key: (checkout: C) => string;
  /** Its shape while it opens (`checkout` is there once opened for a
   *  sign-in the app has not caught up with, see `owner`). `email`: the
   *  fan's address, or where the code just went. */
  shape: (checkout: C | null, fan: { email: string | null }) => ReactNode;
  /** It, open, in the sheet. */
  render: (checkout: C, host: CheckoutHost) => ReactNode;
  /** The sign-in it was opened for (a `buyer.user`): it is that sign-in's
   *  only. Another drops it, back to what is bought; until the app has
   *  caught up with it, its shape stays. Without, it outlives sign-ins. */
  owner?: (checkout: C) => object | null;
  /** Gives it back: it opened after the sheet had gone. */
  release?: (checkout: C) => void;
  /** The sheet went with it up. */
  left?: (checkout: C) => void;
  /** What the button says while it opens, when the purchase waits for
   *  something first ("Getting delivery…"); "Loading checkout" otherwise. */
  openingWords?: () => string | null;
};

/** A purchase, as the one flow runs it (PurchaseSheet). */
export type PurchaseFlow<C> = {
  testID: string;
  /** What is sold, for the words of its end (CHECKOUT_WORDS): tickets, or
   *  an item. */
  goods: CheckoutGoods;
  /** Who keeps the sheet's content above the keyboard (TicketsSheet's
   *  `keyboard`): with "content", the button rides it. */
  keyboard: "sheet" | "content";
  buyer: PurchaseBuyer;
  choice: PurchaseChoice;
  checkout: PurchaseCheckout<C>;
  /** The purchase funnel moved on a step (LIS-1110): the purchase records
   *  it with what it knows (its funnel, artist, access, store). */
  onFunnelStep?: (step: PurchaseFunnelStep) => void;
  /** The same visible opening across a catalog correction's content reset. */
  funnelOpening?: SheetFunnelOpening;
};

type Step<C> =
  | { kind: "choose" }
  | { kind: "opening" }
  | { kind: "checkout"; checkout: C }
  | { kind: "complete"; email: string | null; order: ReactNode };

const CHOOSE = { kind: "choose" } as const;

const isPromise = <T,>(value: T | Promise<T>): value is Promise<T> =>
  typeof (value as { then?: unknown } | null)?.then === "function";

/** The purchase, as the light sheet draws it: its steps in order, under
 *  the sheet's one button. `close` closes the sheet. */
export function PurchaseSheet<C>({
  flow,
  close,
}: {
  flow: PurchaseFlow<C>;
  close: () => void;
}) {
  const { buyer, choice, checkout } = flow;
  const [step, setStep] = useState<Step<C>>(CHOOSE);

  // What answers later (an opening, the sheet going) reads the purchase as
  // it is by then (kept up to date below).
  const latest = useRef({ flow, close, step, email: buyer.email });
  const gone = useRef(false);
  useEffect(() => {
    gone.current = false;
    return () => {
      gone.current = true;
      const { flow: last, step: left } = latest.current;
      if (left.kind === "checkout") last.checkout.left?.(left.checkout);
    };
  }, []);

  // A checkout that is one sign-in's: another sign-in drops it; one the
  // app has not caught up with yet keeps its shape up.
  const owner = checkout.owner;
  const [seen, setSeen] = useState(buyer.user);
  if (owner && seen !== buyer.user) {
    setSeen(buyer.user);
    if (step.kind === "checkout" && owner(step.checkout) === seen)
      setStep(CHOOSE);
  }
  const arriving =
    owner !== undefined &&
    step.kind === "checkout" &&
    owner(step.checkout) !== buyer.user;
  const openKey =
    step.kind === "checkout" && !arriving ? checkout.key(step.checkout) : null;
  const [succeededKey, setSucceededKey] = useState<string | null>(null);
  const succeeded = useCallback(() => setSucceededKey(openKey), [openKey]);

  const settle = useCallback((opened: PurchaseOpened<C>) => {
    const now = latest.current;
    if (gone.current) {
      // Nobody is here for it: what was opened is given back.
      if (opened.kind === "checkout")
        now.flow.checkout.release?.(opened.checkout);
      return;
    }
    if (opened.kind === "close") now.close();
    else if (opened.kind === "back") setStep(CHOOSE);
    else if (opened.kind === "checkout")
      setStep({ kind: "checkout", checkout: opened.checkout });
    else setStep({ kind: "complete", email: now.email, order: opened.order });
  }, []);
  // One opening at a time: a second press before the first has drawn
  // opens nothing more.
  const opening = useRef(false);
  // The funnel's steps, told to the purchase (read as it is by then).
  const funnelRef = useRef<ReturnType<typeof useSheetFunnel> | null>(null);
  const openCheckout = useCallback(() => {
    if (opening.current) return;
    opening.current = true;
    // Gone on from what is bought, signed in: the checkout is asked for.
    funnelRef.current?.choose();
    funnelRef.current?.step({ step: "contact_submit" });
    let opened: PurchaseOpened<C> | Promise<PurchaseOpened<C>>;
    try {
      opened = latest.current.flow.checkout.open();
    } catch {
      opened = { kind: "back" };
    }
    if (!isPromise(opened)) {
      opening.current = false;
      settle(opened);
      return;
    }
    // The checkout's shape at once, while it opens: the sheet sizes to the
    // checkout from here.
    setStep({ kind: "opening" });
    const answered = (result: PurchaseOpened<C>) => {
      opening.current = false;
      settle(result);
    };
    // Failed: back to what is bought, which the purchase has told why.
    opened.then(answered, () => answered({ kind: "back" }));
  }, [settle]);

  const contact = usePurchaseContact({
    accountLoaded: buyer.accountLoaded,
    // Signed in with the code: the account is in, on to the checkout.
    onSignedIn: openCheckout,
  });
  // The fan's address: their sign-in's, or where the code just went.
  const email = buyer.email ?? contact.signIn.challenge?.sentTo ?? null;
  useEffect(() => {
    latest.current = { flow, close, step, email };
  });

  // Where the fan is, as the funnel names it.
  const [delivering, setDelivering] = useState(false);
  const funnel = useSheetFunnel(
    { onFunnelStep: flow.onFunnelStep },
    funnelScreen(step, {
      codeUp: contact.codeUp,
      delivering,
      succeeded: openKey !== null && succeededKey === openKey,
    }),
    { step: "items_choose", ...choice.selection },
    flow.funnelOpening,
  );
  useEffect(() => {
    funnelRef.current = funnel;
  });
  // The code is up: the fan went on with what is bought.
  const { choose } = funnel;
  const codeUp = contact.codeUp;
  useEffect(() => {
    if (codeUp) choose();
  }, [codeUp, choose]);

  const onContinue = () => {
    // Gone on with what is bought, to the sign-in or the checkout.
    choose();
    // Natively the app's sign-in comes first; on app-web the contact step
    // asks (and opens the checkout after).
    if (!buyer.user && Platform.OS !== "web") {
      buyer.signIn();
      return;
    }
    openCheckout();
  };

  // What the checkout says the sheet's button and height should be, for
  // the checkout it was told about (a new one starts from nothing).
  const [chrome, setChrome] = useState<{
    key: string;
    chrome: CheckoutChrome;
  } | null>(null);
  const onChrome = useCallback(
    (next: CheckoutChrome) => {
      if (openKey !== null) setChrome({ key: openKey, chrome: next });
    },
    [openKey],
  );
  const shown = chrome && chrome.key === openKey ? chrome.chrome : null;
  const openingWords = checkout.openingWords?.() ?? null;
  // Nothing may close the sheet while something moves: told by the
  // checkout itself, from the press that starts it.
  const [holding, setHolding] = useState(false);
  // After Buy and before Pay, the purchase's stop: the close asks first
  // whether to use it (Erik, 5 Nov 2026).
  const [stop, setStop] = useState<CloseStop | null>(null);
  const stoppable = useCallback((next: CloseStop | null) => setStop(next), []);
  const back = useCallback(() => setStep(CHOOSE), []);
  const host = useMemo<CheckoutHost>(
    () => ({
      back,
      close,
      chrome: onChrome,
      holding: setHolding,
      stoppable,
      delivering: setDelivering,
      succeeded,
    }),
    [back, close, onChrome, stoppable, succeeded],
  );

  // One button through it all: Continue, then "Loading checkout" while the
  // checkout opens and gets ready, then its Pay and the payment's status.
  // None on a screen that ends the purchase: it has its own.
  const look =
    step.kind === "choose"
      ? continueButton({
          contact,
          ready: choice.ready,
          words: choice.words,
          disabled: choice.disabled,
        })
      : null;
  const button: SheetButtonState | null =
    step.kind === "choose"
      ? look && look.onPress === CONTINUE
        ? { ...look, onPress: onContinue }
        : look
      : step.kind === "complete"
        ? null
        : shown
          ? shown.button
          : step.kind === "opening" && openingWords
            ? { ...LOADING_BUTTON, words: openingWords }
            : LOADING_BUTTON;

  const screen =
    step.kind === "choose" ? (
      <View style={styles.choose} testID="purchase-choice">
        {choice.render({ locked: contact.codeUp })}
        {choice.note}
        {!contact.up || !choice.ready ? null : contact.codeUp ? (
          <CodeStep signIn={contact.signIn} goods={flow.goods} />
        ) : (
          <ContactField signIn={contact.signIn} />
        )}
      </View>
    ) : step.kind === "opening" ? (
      checkout.shape(null, { email })
    ) : step.kind === "complete" ? (
      <PurchaseSuccess
        goods={flow.goods}
        email={step.email}
        order={step.order}
        paid={null}
        onDone={close}
      />
    ) : arriving ? (
      checkout.shape(step.checkout, { email })
    ) : (
      // A new checkout is a new one, from the start.
      <Fragment key={openKey}>{checkout.render(step.checkout, host)}</Fragment>
    );

  return (
    <TicketsSheetFrame
      testID={flow.testID}
      hold={shown?.hold ?? false}
      button={button}
      selecting={step.kind === "choose"}
      after={shown?.after}
      sticky={flow.keyboard === "content" && !shown?.stayPut}
    >
      {openKey !== null && holding ? <CloseGuard stop={stop} /> : null}
      {screen}
    </TicketsSheetFrame>
  );
}

/** The screen the sheet is on, in the funnel's words (`last_screen`). */
function funnelScreen<C>(
  step: Step<C>,
  { codeUp, delivering, succeeded }: {
    codeUp: boolean;
    delivering: boolean;
    succeeded: boolean;
  },
): PurchaseSheetScreen {
  switch (step.kind) {
    case "choose":
      return codeUp ? "contact" : "select";
    case "opening":
      return "checkout";
    case "checkout":
      return succeeded ? "success" : delivering ? "delivery" : "checkout";
    case "complete":
      return "success";
  }
}

/** Mounted only while the checkout says something moves: the sheet will
 *  not close — no swipe, backdrop, back button or programmatic close —
 *  unless the purchase may still be stopped (after Buy, before Pay). Then
 *  the close asks "Stop this purchase?": Yes stops it and the sheet closes,
 *  No closes it and the purchase goes on, as Link in Bio's sheet does. A
 *  stop refused because Pay was claimed first says so, and the sheet stays
 *  on the purchase.
 *
 *  An Alert can be neither changed nor taken down once shown, so when the
 *  view stops offering the stop while it asks, the question gives way: the
 *  sheet stays, "Too late to stop" is said over it where Pay reached the
 *  purchase, and its answer, given later, does nothing. A Yes sends the stop
 *  only if the view offers it then (`stopOnClose`). */
function CloseGuard({ stop }: { stop: CloseStop | null }) {
  const asking = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (stop?.moment === "ask" || !asking.current) return;
    asking.current();
    if (stop?.moment === "too_late") sayTooLate();
  }, [stop]);
  // The guard gone while it asks (nothing moves now): the close it asked
  // about stays undone.
  useEffect(() => () => asking.current?.(), []);
  useOnBeforeClose(({ onConfirm, onCancel }) => {
    if (stop?.moment !== "ask") return false;
    const send = stop.stop;
    let open = true;
    const giveWay = () => {
      open = false;
      asking.current = null;
      onCancel();
    };
    asking.current = giveWay;
    void askStop()
      .then(async (answer) => {
        if (!open) return;
        open = false;
        asking.current = null;
        if (answer === "keep") return onConfirm();
        const stopped = await send();
        if (stopped === "stopped") return onConfirm();
        onCancel();
        if (stopped === "too_late") sayTooLate();
      })
      // Nothing came of the stop: the sheet stays on the purchase.
      .catch(() => onCancel());
  });
  return null;
}

/** Who it is for: app-web, until the fan's account is in, asks for the
 *  address and proves it with a code in the sheet, as on Link in Bio; the
 *  native app asks the fan to sign in instead (Continue). */
function usePurchaseContact({
  accountLoaded,
  onSignedIn,
}: {
  accountLoaded: boolean;
  /** The fan's account is in: the checkout opens. */
  onSignedIn: () => void;
}) {
  const signIn = useSheetSignIn({ onSignedIn });
  const ask = Platform.OS === "web" && !accountLoaded;
  return {
    signIn,
    /** The address (or its code) is asked for in the sheet. */
    up: ask || signIn.stage !== "email",
    /** The code step is up. */
    codeUp: signIn.stage !== "email" && signIn.stage !== "sending",
  };
}

type PurchaseContact = ReturnType<typeof usePurchaseContact>;

/** Where pressing the button is the sheet's Continue: added where the
 *  button is handed on, never while it is drawn. */
const CONTINUE = "continue";

/** The one button while what is bought is up: Continue, or sending the
 *  code first when the address is asked for; none on the code step, where
 *  the sixth digit checks it, nor until what is bought has loaded. */
function continueButton({
  contact,
  ready,
  words,
  disabled,
}: {
  contact: PurchaseContact;
  ready: boolean;
  words: string;
  disabled: boolean;
}):
  | SheetButtonState
  | (Omit<SheetButtonState, "onPress"> & { onPress: typeof CONTINUE })
  | null {
  const { signIn } = contact;
  if (contact.codeUp) return signIn.stage === "signing" ? LOADING_BUTTON : null;
  if (!ready) return null;
  if (contact.up) {
    return {
      words,
      icon: signIn.stage === "sending" ? "spinner" : null,
      plain: false,
      done: false,
      disabled:
        disabled || signIn.stage === "sending" || !isEmailValid(signIn.email),
      onPress: () => void signIn.send(),
    };
  }
  return {
    words,
    icon: null,
    plain: false,
    done: false,
    disabled,
    onPress: CONTINUE,
  };
}

const styles = StyleSheet.create({
  // What is bought, then the contact step: the tickets' own gaps.
  choose: { gap: 12 },
});
